import { ZERO, add } from "@dantria/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { ApiError, api, fmtAmount as fmt, unwrap } from "../api";
import {
  Button,
  ChevronButton,
  DeleteButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  Stat,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";

/** Structural, sensitive access — same rung as the client mutations the API gates at "manager". */
export function canManagePortalAccess(role: string): boolean {
  return role === "manager" || role === "admin" || role === "owner";
}

async function listProjects(slug: string) {
  return unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } }));
}

async function reportsOverview(slug: string) {
  return unwrap(await api.api.c[":slug"].reports.overview.$get({ param: { slug }, query: {} }));
}

interface AddressBody {
  line1?: string | undefined;
  line2?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  postalCode?: string | undefined;
  country?: string | undefined;
}

/** What the form collects. Blank optional fields go up as undefined, not "". */
interface ClientBody {
  name: string;
  email?: string | undefined;
  phone?: string | undefined;
  taxId?: string | undefined;
  billingAddress?: AddressBody | null | undefined;
  paymentTermsDays?: number | undefined;
  notes?: string | undefined;
}

type Client = Awaited<ReturnType<typeof listClients>>["clients"][number];

async function listClients(slug: string) {
  return unwrap(await api.api.c[":slug"].clients.$get({ param: { slug } }));
}

export function Clients({
  slug,
  currency,
  role,
  onOpenProject,
}: {
  slug: string;
  currency: string;
  role: string;
  onOpenProject: (id: string) => void;
}) {
  // `null` is "adding", a row is "editing that one", and undefined is closed —
  // one piece of state, so the form cannot be open in two modes at once.
  const [form, setForm] = useState<Client | null | undefined>(undefined);
  const del = useDeleteRecord(slug);

  // Which clients have their project breakdown open. A set, not a single id —
  // comparing two clients' books side by side is the point of an overview.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const r = useResource({
    key: ["clients", slug],
    list: () => listClients(slug),
    create: async (body: ClientBody) =>
      unwrap(await api.api.c[":slug"].clients.$post({ param: { slug }, json: body })),
    update: async (id: string, body: ClientBody) =>
      unwrap(await api.api.c[":slug"].clients[":id"].$patch({ param: { slug, id }, json: body })),
    // A renamed client shows up on the project list under its new name.
    alsoInvalidate: [["projects", slug]],
  });

  // Same queries — and same cache keys — the Projects list and the Reports
  // overview run, so a client's totals here can never disagree with either.
  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: () => listProjects(slug),
  });
  const overview = useQuery({
    queryKey: ["reports-overview", slug],
    queryFn: () => reportsOverview(slug),
  });

  // Contract value (original plus approved change orders) lives on
  // `profitByProject`; invoiced/paid/outstanding come off the ledger-backed
  // rows. Both are keyed by project, so a project with a signed contract and
  // no billing activity yet still counts its full contract value.
  const contractByProject = new Map(
    (overview.data?.profitByProject ?? []).map((p) => [p.projectId, p.contractValue]),
  );
  const ledgerByProject = new Map((overview.data?.projects ?? []).map((p) => [p.projectId, p]));

  const projectsByClient = new Map<
    string,
    {
      id: string;
      code: string;
      name: string;
      status: string;
      currency: string;
      contractValue: string;
      invoiced: string;
      paid: string;
      outstanding: string;
    }[]
  >();
  for (const p of projects.data?.projects ?? []) {
    const ledger = ledgerByProject.get(p.id);
    const row = {
      id: p.id,
      code: p.code,
      name: p.name,
      status: p.status,
      currency: p.currency,
      contractValue: contractByProject.get(p.id) ?? p.originalContractValue,
      invoiced: ledger?.invoiced ?? "0.00",
      paid: ledger?.cashIn ?? "0.00",
      outstanding: ledger?.ar ?? "0.00",
    };
    const bucket = projectsByClient.get(p.clientId);
    if (bucket) bucket.push(row);
    else projectsByClient.set(p.clientId, [row]);
  }

  function totalsFor(clientId: string) {
    return (projectsByClient.get(clientId) ?? []).reduce(
      (acc, p) => ({
        contract: add(acc.contract, p.contractValue),
        invoiced: add(acc.invoiced, p.invoiced),
        paid: add(acc.paid, p.paid),
        outstanding: add(acc.outstanding, p.outstanding),
      }),
      { contract: ZERO, invoiced: ZERO, paid: ZERO, outstanding: ZERO },
    );
  }
  const totalsByClient = new Map((r.query.data?.clients ?? []).map((c) => [c.id, totalsFor(c.id)]));

  const clientsTable = useTable(r.query.data?.clients ?? [], {
    filter: (c, q) => `${c.name} ${c.email ?? ""}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      name: (c) => c.name.toLowerCase(),
      email: (c) => (c.email ?? "").toLowerCase(),
      terms: (c) => c.paymentTermsDays,
      contract: (c) => Number(totalsByClient.get(c.id)?.contract ?? 0),
      billed: (c) => Number(totalsByClient.get(c.id)?.invoiced ?? 0),
      paid: (c) => Number(totalsByClient.get(c.id)?.paid ?? 0),
      outstanding: (c) => Number(totalsByClient.get(c.id)?.outstanding ?? 0),
    },
  });

  function open(next: Client | null) {
    r.clearErrors();
    setForm(next);
  }

  async function submit(body: ClientBody) {
    try {
      if (form) await r.update({ id: form.id, body });
      else await r.create(body);
      setForm(undefined);
    } catch {
      // Surfaced through r.formError / r.fieldErrors.
    }
  }

  return (
    <Panel
      title="Clients"
      action={
        <Button onClick={() => (form === undefined ? open(null) : setForm(undefined))}>
          {form === undefined ? "New client" : "Cancel"}
        </Button>
      }
    >
      {form !== undefined && (
        <ClientForm
          // Keyed by row, so opening a second client's form doesn't inherit the
          // first one's half-typed values.
          key={form?.id ?? "new"}
          initial={form}
          busy={r.saving}
          formError={r.formError}
          fieldErrors={r.fieldErrors}
          onSubmit={submit}
        />
      )}

      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      {r.query.isPending ? (
        <Loading />
      ) : r.query.isError ? (
        <ErrorNote>{(r.query.error as Error).message}</ErrorNote>
      ) : r.query.data.clients.length === 0 ? (
        <Empty title="No clients yet">Add one before creating a project.</Empty>
      ) : (
        <>
          <TableSearch
            value={clientsTable.query}
            onChange={clientsTable.setQuery}
            placeholder="Filter clients…"
          />
          <Table
            head={
              <>
                <Th
                  sortKey="name"
                  active={clientsTable.sortKey === "name"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Name
                </Th>
                <Th
                  sortKey="email"
                  active={clientsTable.sortKey === "email"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Email
                </Th>
                <Th
                  right
                  sortKey="terms"
                  active={clientsTable.sortKey === "terms"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Terms
                </Th>
                <Th
                  right
                  sortKey="contract"
                  active={clientsTable.sortKey === "contract"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Contract
                </Th>
                <Th
                  right
                  sortKey="billed"
                  active={clientsTable.sortKey === "billed"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Billed
                </Th>
                <Th
                  right
                  sortKey="paid"
                  active={clientsTable.sortKey === "paid"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Paid
                </Th>
                <Th
                  right
                  sortKey="outstanding"
                  active={clientsTable.sortKey === "outstanding"}
                  dir={clientsTable.dir}
                  onSort={clientsTable.toggleSort}
                >
                  Outstanding
                </Th>
                <Th />
              </>
            }
          >
            {clientsTable.rows.map((c) => {
              const t = totalsByClient.get(c.id) ?? {
                contract: ZERO,
                invoiced: ZERO,
                paid: ZERO,
                outstanding: ZERO,
              };
              const clientProjects = projectsByClient.get(c.id) ?? [];
              const isOpen = expanded.has(c.id);
              return (
                <Fragment key={c.id}>
                  <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                    <Td>{c.name}</Td>
                    <Td muted>{c.email ?? "—"}</Td>
                    <Td right mono muted>
                      {c.paymentTermsDays}d
                    </Td>
                    <Td right mono>
                      {fmt(t.contract, currency)}
                    </Td>
                    <Td right mono muted>
                      {fmt(t.invoiced, currency)}
                    </Td>
                    <Td right mono>
                      {fmt(t.paid, currency)}
                    </Td>
                    <Td right mono>
                      {fmt(t.outstanding, currency)}
                    </Td>
                    <Td right>
                      <div className="flex items-center justify-end gap-1">
                        <ChevronButton
                          open={isOpen}
                          onClick={() => toggleExpanded(c.id)}
                          label="projects and portal access"
                        />
                        <Button variant="ghost" onClick={() => open(c)}>
                          Edit
                        </Button>
                        {role === "owner" && (
                          <DeleteButton
                            busy={del.deleting}
                            onConfirm={() => del.remove("client", c.id)}
                          />
                        )}
                      </div>
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={8} className="bg-neutral-50 px-4 py-4">
                        {canManagePortalAccess(role) && (
                          <PortalAccess slug={slug} clientId={c.id} email={c.email} />
                        )}

                        {clientProjects.length === 0 ? (
                          <Empty title="No projects yet">Nothing to show for this client.</Empty>
                        ) : (
                          <>
                            <div className="mb-3 grid gap-3 sm:grid-cols-4">
                              <Stat
                                label="Contract"
                                value={fmt(t.contract)}
                                currency={currency}
                                hint="Across every project"
                              />
                              <Stat label="Billed" value={fmt(t.invoiced)} currency={currency} />
                              <Stat label="Paid" value={fmt(t.paid)} currency={currency} />
                              <Stat
                                label="Outstanding"
                                value={fmt(t.outstanding)}
                                currency={currency}
                                hint="Billed, not yet paid"
                              />
                            </div>
                            <Table
                              maxHeight={false}
                              head={
                                <>
                                  <Th>Code</Th>
                                  <Th>Project</Th>
                                  <Th>Status</Th>
                                  <Th right>Contract</Th>
                                  <Th right>Billed</Th>
                                  <Th right>Paid</Th>
                                  <Th right>Outstanding</Th>
                                </>
                              }
                            >
                              {clientProjects.map((p) => (
                                <tr
                                  key={p.id}
                                  tabIndex={0}
                                  onClick={() => onOpenProject(p.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") onOpenProject(p.id);
                                  }}
                                  className="cursor-pointer border-b border-neutral-100 last:border-0 even:bg-white transition-colors hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
                                >
                                  <Td mono muted>
                                    {p.code}
                                  </Td>
                                  <Td>{p.name}</Td>
                                  <Td muted>{p.status.replace(/_/g, " ")}</Td>
                                  <Td right mono>
                                    {fmt(p.contractValue, p.currency)}
                                  </Td>
                                  <Td right mono muted>
                                    {fmt(p.invoiced, p.currency)}
                                  </Td>
                                  <Td right mono>
                                    {fmt(p.paid, p.currency)}
                                  </Td>
                                  <Td right mono>
                                    {fmt(p.outstanding, p.currency)}
                                  </Td>
                                </tr>
                              ))}
                            </Table>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>
        </>
      )}
    </Panel>
  );
}

/**
 * Who can sign in to view this client's billing from outside the company —
 * the client-portal counterpart to the staff invitations panel in
 * Settings.tsx, scoped to one client instead of the whole company.
 *
 * Sends to the client's own address on file rather than a typed-in one: the
 * contact a portal login should reach is whoever the company already has an
 * email for, not a second address someone has to remember and keep in sync.
 * With none on file there's nothing to send to, so the action is a note
 * pointing at "Edit" instead of a form with nowhere to go.
 */
export function PortalAccess({
  slug,
  clientId,
  email,
}: {
  slug: string;
  clientId: string;
  email: string | null;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ email: string; link: string; mailed: boolean } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const key = ["client-portal-invitations", slug, clientId];
  const q = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].clients[":id"]["portal-invitations"].$get({
          param: { slug, id: clientId },
        }),
      ),
  });

  async function invite() {
    if (!email) return;
    setError(null);
    setBusy(true);
    try {
      const body = await unwrap(
        await api.api.c[":slug"].clients[":id"]["portal-invitations"].$post({
          param: { slug, id: clientId },
          json: { email },
        }),
      );
      setCopied(false);
      setIssued({
        email: body.invitation?.email ?? "",
        link: body.link,
        mailed: body.mailed === true,
      });
      qc.invalidateQueries({ queryKey: key });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(invId: string) {
    await unwrap(
      await api.api.c[":slug"].clients[":id"]["portal-invitations"][":invId"].revoke.$post({
        param: { slug, id: clientId, invId },
      }),
    );
    qc.invalidateQueries({ queryKey: key });
  }

  const pending = (q.data?.invitations ?? []).filter((i) => !i.acceptedAt);
  const active = (q.data?.portalUsers ?? []).filter((u) => !u.archivedAt);

  return (
    <div className="mb-4 border-b border-neutral-200 pb-4">
      <h3 className="mb-2 text-sm font-medium text-neutral-700">Portal access</h3>
      <p className="mb-3 max-w-prose text-xs text-neutral-500">
        Send this client a sign-in link to see this same billing summary on their own — contract,
        billed, paid, and outstanding, nothing about cost or margin.
      </p>

      {email ? (
        <Button onClick={invite} disabled={busy}>
          {busy ? "Sending…" : `Send portal access to ${email}`}
        </Button>
      ) : (
        <p className="text-xs text-neutral-500">
          This client has no email on file — add one under "Edit" before sending portal access.
        </p>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {issued && (
        <div className="mt-3 rounded-xl border border-accent-500/40 bg-accent-50/50 p-4 shadow-sm">
          <p className="text-sm">
            Invitation for <span className="font-medium">{issued.email}</span>.{" "}
            {issued.mailed
              ? "An email is on its way. Here's the link too, in case it doesn't arrive — it won't be shown again."
              : "Mail isn't configured, so send them this link yourself — it won't be shown again."}
          </p>
          <div className="mt-2 flex gap-2">
            <input className={input} readOnly value={issued.link} />
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(issued.link);
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      {(pending.length > 0 || active.length > 0) && (
        <div className="mt-3 space-y-1 text-sm">
          {active.map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded-lg px-2 py-1">
              <span>
                {u.email} <span className="text-neutral-400">— active</span>
              </span>
            </div>
          ))}
          {pending.map((i) => (
            <div
              key={i.id}
              className="flex items-center justify-between rounded-lg px-2 py-1 hover:bg-white"
            >
              <span>
                {i.email} <span className="text-neutral-400">— invited, not yet accepted</span>
              </span>
              <Button variant="ghost" onClick={() => revoke(i.id)}>
                Revoke
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One form for both jobs. A client's details are the same fields whether you
 * are entering them for the first time or fixing a typo in them, and two forms
 * would be two places to add the next field to.
 */
function ClientForm({
  initial,
  busy,
  formError,
  fieldErrors,
  onSubmit,
}: {
  initial: Client | null;
  busy: boolean;
  formError: string | null;
  fieldErrors: Record<string, string>;
  onSubmit: (body: ClientBody) => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [taxId, setTaxId] = useState(initial?.taxId ?? "");
  const initialAddress = (initial?.billingAddress ?? null) as AddressBody | null;
  const [line1, setLine1] = useState(initialAddress?.line1 ?? "");
  const [line2, setLine2] = useState(initialAddress?.line2 ?? "");
  const [city, setCity] = useState(initialAddress?.city ?? "");
  const [addrState, setAddrState] = useState(initialAddress?.state ?? "");
  const [postalCode, setPostalCode] = useState(initialAddress?.postalCode ?? "");
  const [country, setCountry] = useState(initialAddress?.country ?? "");
  const [terms, setTerms] = useState(String(initial?.paymentTermsDays ?? 30));
  const [notes, setNotes] = useState(initial?.notes ?? "");

  return (
    <form
      className="mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        const address = {
          line1: line1.trim() || undefined,
          line2: line2.trim() || undefined,
          city: city.trim() || undefined,
          state: addrState.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          country: country.trim() || undefined,
        };
        const hasAddress = Object.values(address).some((v) => v !== undefined);
        onSubmit({
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          taxId: taxId.trim() || undefined,
          billingAddress: hasAddress ? address : initialAddress ? null : undefined,
          paymentTermsDays: Number(terms),
          notes: notes.trim() || undefined,
        });
      }}
    >
      {formError && <ErrorNote>{formError}</ErrorNote>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Name" error={fieldErrors.name}>
          <input
            className={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Field>
        <Field label="Email" error={fieldErrors.email}>
          <input
            className={input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Phone" error={fieldErrors.phone}>
          <input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Tax ID" error={fieldErrors.taxId}>
          <input className={input} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
        </Field>
        <Field label="Address line 1">
          <input className={input} value={line1} onChange={(e) => setLine1(e.target.value)} />
        </Field>
        <Field label="Address line 2">
          <input className={input} value={line2} onChange={(e) => setLine2(e.target.value)} />
        </Field>
        <Field label="City">
          <input className={input} value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label="State / province">
          <input
            className={input}
            value={addrState}
            onChange={(e) => setAddrState(e.target.value)}
          />
        </Field>
        <Field label="Postal code">
          <input
            className={input}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
          />
        </Field>
        <Field label="Country">
          <input className={input} value={country} onChange={(e) => setCountry(e.target.value)} />
        </Field>
        <Field label="Payment terms" hint="Days" error={fieldErrors.paymentTermsDays}>
          <input
            className={input}
            type="number"
            min={0}
            max={365}
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
          />
        </Field>
        <Field label="Notes" error={fieldErrors.notes}>
          <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : initial ? "Save changes" : "Add client"}
        </Button>
      </div>
    </form>
  );
}
